import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Users, 
  Edit2, 
  Trash2, 
  Plus, 
  Shield, 
  ShieldCheck, 
  Key,
  Search,
  Eye,
  EyeOff
} from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/Button';

const AdminUsers = () => {
  const { language, isRTL } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    isAdmin: false
  });
  
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(language === 'en' ? 'Failed to load users' : 'נכשל בטעינת המשתמשים');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userForm.username.trim() || !userForm.email.trim()) {
      toast.error(language === 'en' ? 'Username and email are required' : 'שם משתמש ואימייל נדרשים');
      return;
    }

    if (!editingUser && !userForm.password.trim()) {
      toast.error(language === 'en' ? 'Password is required for new users' : 'סיסמה נדרשת למשתמשים חדשים');
      return;
    }

    try {
      if (editingUser) {
        await axios.put(`/users/${editingUser._id}`, {
          username: userForm.username,
          email: userForm.email,
          isAdmin: userForm.isAdmin
        });
        toast.success(language === 'en' ? 'User updated successfully!' : 'המשתמש עודכן בהצלחה!');
      } else {
        await axios.post('/users/admin', userForm);
        toast.success(language === 'en' ? 'User created successfully!' : 'המשתמש נוצר בהצלחה!');
      }
      
      setShowModal(false);
      setEditingUser(null);
      setUserForm({ username: '', email: '', password: '', isAdmin: false });
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.message || (language === 'en' ? 'Failed to save user' : 'נכשל בשמירת המשתמש');
      toast.error(message);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.newPassword.trim()) {
      toast.error(language === 'en' ? 'New password is required' : 'סיסמה חדשה נדרשת');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(language === 'en' ? 'Passwords do not match' : 'הסיסמאות לא תואמות');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error(language === 'en' ? 'Password must be at least 6 characters' : 'הסיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }

    try {
      await axios.put(`/auth/admin/change-user-password/${changingPasswordUser._id}`, {
        newPassword: passwordForm.newPassword
      });
      
      toast.success(language === 'en' 
        ? `Password changed successfully for ${changingPasswordUser.username}` 
        : `הסיסמה שונתה בהצלחה עבור ${changingPasswordUser.username}`);
      
      setShowPasswordModal(false);
      setChangingPasswordUser(null);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.message || (language === 'en' ? 'Failed to change password' : 'נכשל בשינוי הסיסמה');
      toast.error(message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      email: user.email,
      password: '',
      isAdmin: user.isAdmin
    });
    setShowModal(true);
  };

  const handleChangePassword = (user) => {
    setChangingPasswordUser(user);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const handleDelete = async (userId, username) => {
    if (window.confirm(language === 'en' 
      ? `Are you sure you want to delete user "${username}"?` 
      : `האם אתה בטוח שברצונך למחוק את המשתמש "${username}"?`)) {
      try {
        await axios.delete(`/users/${userId}`);
        toast.success(language === 'en' ? 'User deleted successfully!' : 'המשתמש נמחק בהצלחה!');
        fetchUsers();
      } catch (error) {
        const message = error.response?.data?.message || (language === 'en' ? 'Failed to delete user' : 'נכשל במחיקת המשתמש');
        toast.error(message);
      }
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout
        title={language === 'en' ? 'User management' : 'ניהול משתמשים'}
        subtitle={language === 'en' ? 'Accounts and roles' : 'חשבונות והרשאות'}
        loading
      />
    );
  }

  return (
    <AdminLayout
      title={language === 'en' ? 'User management' : 'ניהול משתמשים'}
      subtitle={`${filteredUsers.length} ${language === 'en' ? 'users' : 'משתמשים'}`}
      actions={
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setEditingUser(null);
            setUserForm({ username: '', email: '', password: '', isAdmin: false });
            setShowModal(true);
          }}
        >
          <Plus className="h-4 w-4" />
          {language === 'en' ? 'Add user' : 'הוסף משתמש'}
        </Button>
      }
    >
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search
              className={`pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 ${
                isRTL ? 'right-3' : 'left-3'
              }`}
            />
            <input
              type="text"
              placeholder={language === 'en' ? 'Search users...' : 'חפש משתמשים...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              dir={isRTL ? 'rtl' : 'ltr'}
              className={`w-full rounded-xl border border-slate-200 py-3 text-base text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 ${
                isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'
              }`}
            />
          </div>
        </div>

        {/* Users List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <div key={user._id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 16, 
                padding: 20, 
                background: '#f8fafc', 
                borderRadius: 16,
                border: '2px solid #fee2e2'
              }}>
                {/* User Avatar */}
                <div style={{ 
                  width: 48, 
                  height: 48, 
                  background: user.isAdmin ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  color: 'white', 
                  fontWeight: 700, 
                  fontSize: 18,
                  flexShrink: 0
                }}>
                  {user.username.charAt(0).toUpperCase()}
                </div>
                
                {/* User Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: '#18181b', margin: 0 }}>
                      {user.username}
                    </h3>
                    {user.isAdmin && (
                      <div style={{ 
                        background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)', 
                        color: 'white', 
                        padding: '2px 8px', 
                        borderRadius: 6, 
                        fontSize: 12, 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        <ShieldCheck style={{ width: 12, height: 12 }} />
                        {language === 'en' ? 'Admin' : 'מנהל'}
                      </div>
                    )}
                  </div>
                  <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 4 }}>
                    {user.email}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: 12 }}>
                    {language === 'en' ? 'Total Points:' : 'נקודות כולל:'} {user.totalPoints}
                  </div>
                </div>
                
                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    onClick={() => handleEdit(user)}
                    style={{ 
                      background: '#fee2e2', 
                      color: '#991b1b', 
                      border: 'none', 
                      borderRadius: 8, 
                      padding: 8, 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={language === 'en' ? 'Edit user' : 'ערוך משתמש'}
                  >
                    <Edit2 style={{ width: 16, height: 16 }} />
                  </button>
                  
                  <button
                    onClick={() => handleChangePassword(user)}
                    style={{ 
                      background: '#fef3c7', 
                      color: '#d97706', 
                      border: 'none', 
                      borderRadius: 8, 
                      padding: 8, 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={language === 'en' ? 'Change password' : 'שנה סיסמה'}
                  >
                    <Key style={{ width: 16, height: 16 }} />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(user._id, user.username)}
                    style={{ 
                      background: '#fee2e2', 
                      color: '#dc2626', 
                      border: 'none', 
                      borderRadius: 8, 
                      padding: 8, 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={language === 'en' ? 'Delete user' : 'מחק משתמש'}
                  >
                    <Trash2 style={{ width: 16, height: 16 }} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div style={{ 
              textAlign: 'center', 
              color: '#a1a1aa', 
              padding: '64px 0',
              background: '#f8fafc',
              borderRadius: 16
            }}>
              <Users style={{ width: 64, height: 64, color: '#fee2e2', marginBottom: 16 }} />
              <div style={{ fontSize: 20, marginBottom: 8 }}>
                {language === 'en' ? 'No users found' : 'לא נמצאו משתמשים'}
              </div>
              <div style={{ fontSize: 16, color: '#6b7280' }}>
                {searchTerm ? 
                  (language === 'en' ? 'Try adjusting your search terms' : 'נסה לשנות את תנאי החיפוש') :
                  (language === 'en' ? 'No users available' : 'אין משתמשים זמינים')
                }
              </div>
            </div>
          )}
        </div>

        {/* User Modal */}
        {showModal && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}>
            <div style={{ 
              background: 'white', 
              borderRadius: 16, 
              padding: 32, 
              width: '90%', 
              maxWidth: 500,
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: '#18181b', marginBottom: 24 }}>
                {editingUser ? 
                  (language === 'en' ? 'Edit User' : 'ערוך משתמש') : 
                  (language === 'en' ? 'Add New User' : 'הוסף משתמש חדש')
                }
              </h2>
              
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b' }}>
                    {language === 'en' ? 'Username' : 'שם משתמש'}
                  </label>
                  <input
                    type="text"
                    value={userForm.username}
                    onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #fee2e2',
                      borderRadius: 8,
                      fontSize: 16,
                      outline: 'none',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}
                    required
                  />
                </div>
                
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b' }}>
                    {language === 'en' ? 'Email' : 'אימייל'}
                  </label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #fee2e2',
                      borderRadius: 8,
                      fontSize: 16,
                      outline: 'none',
                      direction: 'ltr'
                    }}
                    required
                  />
                </div>
                
                {!editingUser && (
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b' }}>
                      {language === 'en' ? 'Password' : 'סיסמה'}
                    </label>
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #fee2e2',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        direction: 'ltr'
                      }}
                      required
                      minLength={6}
                    />
                  </div>
                )}
                
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userForm.isAdmin}
                      onChange={(e) => setUserForm({ ...userForm, isAdmin: e.target.checked })}
                      style={{ width: 16, height: 16 }}
                    />
                    <Shield style={{ width: 16, height: 16, color: '#f59e0b' }} />
                    <span style={{ fontWeight: 600, color: '#18181b' }}>
                      {language === 'en' ? 'Admin User' : 'משתמש מנהל'}
                    </span>
                  </label>
                </div>
                
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{ 
                      background: '#f3f4f6', 
                      color: '#374151', 
                      fontWeight: 600, 
                      padding: '12px 24px', 
                      borderRadius: 8, 
                      border: 'none', 
                      cursor: 'pointer' 
                    }}
                  >
                    {language === 'en' ? 'Cancel' : 'ביטול'}
                  </button>
                  <button
                    type="submit"
                    style={{ 
                      background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)', 
                      color: 'white', 
                      fontWeight: 600, 
                      padding: '12px 24px', 
                      borderRadius: 8, 
                      border: 'none', 
                      cursor: 'pointer' 
                    }}
                  >
                    {editingUser ? 
                      (language === 'en' ? 'Update User' : 'עדכן משתמש') : 
                      (language === 'en' ? 'Create User' : 'צור משתמש')
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && changingPasswordUser && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1000 
          }}>
            <div style={{ 
              background: 'white', 
              borderRadius: 16, 
              padding: 32, 
              width: '90%', 
              maxWidth: 400,
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: '#18181b', marginBottom: 8 }}>
                {language === 'en' ? 'Change Password' : 'שנה סיסמה'}
              </h2>
              <p style={{ color: '#6b7280', marginBottom: 24 }}>
                {language === 'en' ? `Changing password for: ${changingPasswordUser.username}` : `משנה סיסמה עבור: ${changingPasswordUser.username}`}
              </p>
              
              <form onSubmit={handlePasswordChange}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b' }}>
                    {language === 'en' ? 'New Password' : 'סיסמה חדשה'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        paddingRight: isRTL ? 16 : 44,
                        paddingLeft: isRTL ? 44 : 16,
                        border: '2px solid #fee2e2',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        direction: 'ltr'
                      }}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: isRTL ? 'auto' : 12,
                        left: isRTL ? 12 : 'auto',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6b7280'
                      }}
                    >
                      {showPassword ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
                    </button>
                  </div>
                </div>
                
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#18181b' }}>
                    {language === 'en' ? 'Confirm New Password' : 'אשר סיסמה חדשה'}
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #fee2e2',
                      borderRadius: 8,
                      fontSize: 16,
                      outline: 'none',
                      direction: 'ltr'
                    }}
                    required
                    minLength={6}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setChangingPasswordUser(null);
                      setPasswordForm({ newPassword: '', confirmPassword: '' });
                    }}
                    style={{ 
                      background: '#f3f4f6', 
                      color: '#374151', 
                      fontWeight: 600, 
                      padding: '12px 24px', 
                      borderRadius: 8, 
                      border: 'none', 
                      cursor: 'pointer' 
                    }}
                  >
                    {language === 'en' ? 'Cancel' : 'ביטול'}
                  </button>
                  <button
                    type="submit"
                    style={{ 
                      background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)', 
                      color: 'white', 
                      fontWeight: 600, 
                      padding: '12px 24px', 
                      borderRadius: 8, 
                      border: 'none', 
                      cursor: 'pointer' 
                    }}
                  >
                    {language === 'en' ? 'Change Password' : 'שנה סיסמה'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
    </AdminLayout>
  );
};

export default AdminUsers;
